'use client'

import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'

/** localStorage throws in some privacy modes; a coach-mark must not care. */
function readSeen(key: string): boolean {
  try {
    return localStorage.getItem(key) !== null
  } catch {
    return false
  }
}

function writeSeen(key: string): void {
  try {
    localStorage.setItem(key, 'true')
  } catch {
    // Non-fatal: the tooltip shows again next visit rather than never dismissing
  }
}

interface ScrollTooltipProps {
  children: React.ReactNode
  content: string
  scrollThreshold?: number
  autoDismissDelay?: number
  storageKey?: string
}

export function ScrollTooltip({
  children,
  content,
  scrollThreshold = 200,
  autoDismissDelay = 5000,
  storageKey = 'theme-tooltip-seen',
}: ScrollTooltipProps) {
  const [isVisible, setIsVisible] = useState(false)
  const [coords, setCoords] = useState({ top: 0, left: 0 })
  const triggerRef = useRef<HTMLDivElement>(null)
  const [mounted, setMounted] = useState(false)
  const dismissTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const hasShownRef = useRef(false)

  useEffect(() => {
    setMounted(true)
    return () => setMounted(false)
  }, [])

  // The scroll listener owns the show/auto-dismiss lifecycle. `isVisible` must
  // stay out of this dependency array: the effect sets it, so including it
  // re-runs the effect and the cleanup clears the auto-dismiss timer it just
  // scheduled, which is why the coach-mark used to stay pinned forever.
  useEffect(() => {
    const handleScroll = () => {
      // Once per visitor: hasShownRef guards this session, localStorage the rest
      if (hasShownRef.current) return

      if (readSeen(storageKey)) {
        hasShownRef.current = true
        return
      }

      if (window.scrollY <= scrollThreshold) return

      // Bail without consuming the once-per-visitor key when the trigger is
      // not on screen — otherwise the coach-mark is silently spent on a
      // viewport that never showed it.
      if (!updatePosition()) return

      // The dismissal timer is scheduled before anything that can throw.
      // Storage is unavailable in some privacy modes, and letting it abort
      // this handler is what would pin the coach-mark open forever.
      if (autoDismissDelay > 0) {
        dismissTimeoutRef.current = setTimeout(
          () => setIsVisible(false),
          autoDismissDelay
        )
      }

      setIsVisible(true)
      hasShownRef.current = true
      writeSeen(storageKey)
    }

    window.addEventListener('scroll', handleScroll)

    return () => {
      window.removeEventListener('scroll', handleScroll)
      if (dismissTimeoutRef.current) {
        clearTimeout(dismissTimeoutRef.current)
      }
    }
  }, [scrollThreshold, autoDismissDelay, storageKey])

  /**
   * Positions the tooltip under its trigger and reports whether the trigger is
   * actually painted. A zero-size rect means the control is display:none at
   * this viewport (the header's theme toggle only exists from `xl` up), and a
   * coach-mark for an invisible control must not render — it would land in the
   * top-left corner over whatever is there.
   */
  const updatePosition = () => {
    const rect = triggerRef.current?.getBoundingClientRect()
    if (!rect || rect.width === 0 || rect.height === 0) return false

    // A rect reflects transforms, so the hide-on-scroll header still reports a
    // full-size box while translated above the viewport. Size alone therefore
    // is not "visible": without this the coach-mark painted off-screen at a
    // negative top AND spent its once-per-visitor key doing it. Bailing here
    // (before writeSeen) leaves the key intact, so it fires on the first
    // upward scroll instead — exactly when the toggle is back on screen.
    if (rect.bottom <= 0 || rect.top >= window.innerHeight) return false

    const tooltipOffset = 8
    setCoords({
      top: rect.bottom + tooltipOffset,
      left: rect.left + rect.width / 2,
    })
    return true
  }

  const handleDismiss = () => {
    setIsVisible(false)
    if (dismissTimeoutRef.current) {
      clearTimeout(dismissTimeoutRef.current)
    }
  }

  return (
    <>
      <div ref={triggerRef} onClick={handleDismiss} className='inline-flex'>
        {children}
      </div>
      {mounted &&
        createPortal(
          <div
            style={{
              position: 'fixed',
              top: `${coords.top}px`,
              left: `${coords.left}px`,
              transform: `translateX(-50%) ${isVisible ? 'translateY(0)' : 'translateY(-10px)'}`,
              opacity: isVisible ? 1 : 0,
              transition:
                'opacity 0.3s ease-in-out, transform 0.3s ease-in-out',
              zIndex: 9999,
              pointerEvents: isVisible ? 'auto' : 'none',
            }}
            className='flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md bg-growth text-white shadow-lg cursor-pointer'
            role='tooltip'
            // Stays mounted so both transitions play, but leaves the
            // accessibility tree the moment it is visually gone.
            aria-hidden={!isVisible}
            onClick={handleDismiss}
          >
            {content}
            {/* Visual dismiss affordance. Deliberately aria-hidden and not a
                button: role='tooltip' must not contain focusable content, and
                the coach-mark auto-dismisses for keyboard users. */}
            <span aria-hidden='true' className='opacity-70'>
              &times;
            </span>
            <div className='absolute w-2 h-2 bg-growth transform rotate-45 top-[-4px] left-1/2 -translate-x-1/2' />
          </div>,
          document.body
        )}
    </>
  )
}
